import { useCallback, useEffect, useMemo, useState, Fragment } from 'react';
import {
  Alert, Box, Button, Chip, Collapse, LinearProgress, MenuItem, Pagination,
  Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import IconButton from '@mui/material/IconButton';
import { getAuditEntities, getAuditLogs } from '../../services/api';
import {
  escapeHtml as html,
  loadReportCompany,
  reportBrandHtml,
  REPORT_BRAND_CSS,
} from '../../utils/reportBranding';
import {
  montarAlteracoes,
  montarCamposLista,
  nomeResponsavel,
} from '../../utils/auditFormat';
import PageHeader from '../common/PageHeader';
import ResponsiveTable from '../common/ResponsiveTable';
import SortableTh from '../common/SortableTh';
import { useClientSort } from '../../hooks/useClientSort';
import { toDateInputValue } from '../../utils/datetimeLocal';

const localDate = toDateInputValue;

const ACAO_LABEL = {
  INSERT: 'Inclusão',
  UPDATE: 'Alteração',
  DELETE: 'Exclusão',
};

const ENTIDADE_LABEL = {
  Produto: 'Produto',
  ProdutoPreco: 'Preço de produto',
  Cliente: 'Cliente',
  Venda: 'Venda',
  VendaItem: 'Item de venda',
  ConvenioLancamento: 'Convênio (lançamento)',
  ConvenioRecebimento: 'Convênio (recebimento)',
  Fornecedor: 'Fornecedor',
  Categoria: 'Categoria',
  SubCategoria: 'Subcategoria',
  FormaPagamento: 'Forma de pagamento',
  Usuario: 'Usuário',
  GrupoTributario: 'Grupo tributário',
  Estabelecimento: 'Estabelecimento',
  PDV: 'PDV / Terminal',
  MovimentoEstoque: 'Movimento de estoque',
  PreVenda: 'Pré-venda',
  Campanha: 'Campanha',
  Permissao: 'Perfil de permissão',
};

function labelModulo(nome) {
  return ENTIDADE_LABEL[nome] || nome || '—';
}

function AlteracoesDetalhe({ item }) {
  const alteracoes = montarAlteracoes(item);
  const acao = (item.acao || '').toUpperCase();

  if (alteracoes.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        Nenhum detalhe disponível para exibição.
      </Typography>
    );
  }

  if (acao === 'UPDATE') {
    return (
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Campo</TableCell>
            <TableCell>Antes</TableCell>
            <TableCell>Depois</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {alteracoes.map((a) => (
            <TableRow key={a.campo}>
              <TableCell sx={{ fontWeight: 600 }}>{a.label}</TableCell>
              <TableCell sx={{ color: 'error.main' }}>{a.antes}</TableCell>
              <TableCell sx={{ color: 'success.main', fontWeight: 600 }}>{a.depois}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Campo</TableCell>
          <TableCell>Valor</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {alteracoes.map((a) => (
          <TableRow key={a.campo}>
            <TableCell sx={{ fontWeight: 600 }}>{a.label}</TableCell>
            <TableCell>{a.depois || a.antes}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export default function AuditReport() {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth(), 1);
  const [filters, setFilters] = useState({
    de: localDate(first),
    ate: localDate(now),
    entidade: '',
    acao: '',
    termo: '',
  });
  const [entidades, setEntidades] = useState([]);
  const [data, setData] = useState(null);
  const [pagina, setPagina] = useState(1);
  const [qtde, setQtde] = useState(25);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const { data: lista } = await getAuditEntities();
        setEntidades(Array.isArray(lista) ? lista : []);
      } catch {
        setEntidades([]);
      }
    })();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {
        de: filters.de,
        ate: filters.ate,
        pagina,
        qtde,
      };
      if (filters.entidade) params.entidade = filters.entidade;
      if (filters.acao) params.acao = filters.acao;
      if (filters.termo) params.termo = filters.termo;
      const { data: resp } = await getAuditLogs(params);
      setData(resp);
      setExpanded({});
    } catch (e) {
      setError(e?.response?.data?.error || 'Não foi possível carregar o log de auditoria.');
    } finally {
      setLoading(false);
    }
  }, [filters, pagina, qtde]);

  useEffect(() => { load(); }, [load]);

  const exportPdf = async () => {
    const popup = window.open('', '_blank', 'width=1200,height=800');
    if (!popup) {
      setError('Libere pop-ups para gerar o PDF.');
      return;
    }
    try {
      const company = await loadReportCompany();
      const rows = (data?.items || [])
        .map((x) => {
          const campos = montarCamposLista(x);
          const detalhe = montarAlteracoes(x).map((a) => {
            if (a.antes != null && a.depois != null) {
              return `${html(a.label)}: ${html(a.antes)} → ${html(a.depois)}`;
            }
            return `${html(a.label)}: ${html(a.depois || a.antes)}`;
          }).join('<br/>') || '—';
          return `<tr>
          <td>${html(new Date(x.dataHoraUtc).toLocaleString('pt-BR'))}</td>
          <td>${html(nomeResponsavel(x))}</td>
          <td>${html(labelModulo(x.entidadeNome))}${x.entidadeId ? ` #${html(x.entidadeId)}` : ''}</td>
          <td>${html(ACAO_LABEL[x.acao?.toUpperCase()] || x.acao)}</td>
          <td>${html(campos)}</td>
          <td>${detalhe}</td>
        </tr>`;
        })
        .join('');
      popup.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Auditoria</title>
        <style>
          body{font:10px Arial;padding:18px;color:#172033}
          ${REPORT_BRAND_CSS}
          table{width:100%;border-collapse:collapse;margin-top:12px}
          th,td{border-bottom:1px solid #e2e6ed;padding:6px;text-align:left;vertical-align:top}
          th{background:#eaf6fe}
        </style></head><body>
        ${reportBrandHtml(company, { title: 'Relatório de auditoria' })}
        <div style="margin-top:8px">Período: ${html(filters.de)} a ${html(filters.ate)}</div>
        <div>Registros nesta página: ${html((data?.items || []).length)} · Total: ${html(data?.totalItems || 0)}</div>
        <table><thead><tr>
          <th>Data</th><th>Responsável</th><th>Módulo</th><th>Ação</th><th>Campos</th><th>Detalhe</th>
        </tr></thead>
        <tbody>${rows}</tbody></table></body></html>`);
      popup.document.close();
      setTimeout(() => popup.print(), 300);
    } catch (e) {
      popup.close();
      setError('Falha ao gerar PDF.');
    }
  };

  const acaoColor = (acao) => {
    const a = (acao || '').toUpperCase();
    if (a === 'INSERT') return 'success';
    if (a === 'DELETE') return 'error';
    return 'warning';
  };

  const auditAccessors = useMemo(
    () => ({
      dataHoraUtc: (x) => Date.parse(x.dataHoraUtc) || 0,
      responsavel: (x) => nomeResponsavel(x),
      modulo: (x) => labelModulo(x.entidadeNome),
      campos: (x) => montarCamposLista(x),
    }),
    [],
  );
  const { sorted, orderBy, order, requestSort } = useClientSort(
    data?.items || [],
    'dataHoraUtc',
    'desc',
    auditAccessors,
  );

  const totalPages = data?.totalPages || 1;
  const totalItems = data?.totalItems || 0;
  const inicio = totalItems === 0 ? 0 : (pagina - 1) * qtde + 1;
  const fim = Math.min(pagina * qtde, totalItems);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <PageHeader
        title="Auditoria"
        subtitle="Quem alterou o quê: módulo, ação, campos e responsável."
      />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} flexWrap="wrap" useFlexGap>
          <TextField label="De" type="date" size="small" value={filters.de}
            onChange={(e) => { setPagina(1); setFilters((f) => ({ ...f, de: e.target.value })); }} InputLabelProps={{ shrink: true }} />
          <TextField label="Até" type="date" size="small" value={filters.ate}
            onChange={(e) => { setPagina(1); setFilters((f) => ({ ...f, ate: e.target.value })); }} InputLabelProps={{ shrink: true }} />
          <TextField select label="Módulo" size="small" value={filters.entidade} sx={{ minWidth: 200 }}
            onChange={(e) => { setPagina(1); setFilters((f) => ({ ...f, entidade: e.target.value })); }}>
            <MenuItem value="">Todos</MenuItem>
            {entidades.map((e) => (
              <MenuItem key={e} value={e}>{labelModulo(e)}</MenuItem>
            ))}
          </TextField>
          <TextField select label="Ação" size="small" value={filters.acao} sx={{ minWidth: 140 }}
            onChange={(e) => { setPagina(1); setFilters((f) => ({ ...f, acao: e.target.value })); }}>
            <MenuItem value="">Todas</MenuItem>
            <MenuItem value="INSERT">Inclusão</MenuItem>
            <MenuItem value="UPDATE">Alteração</MenuItem>
            <MenuItem value="DELETE">Exclusão</MenuItem>
          </TextField>
          <TextField label="Buscar no conteúdo" size="small" value={filters.termo}
            onChange={(e) => { setPagina(1); setFilters((f) => ({ ...f, termo: e.target.value })); }} />
          <TextField select label="Por página" size="small" value={qtde} sx={{ minWidth: 110 }}
            onChange={(e) => { setPagina(1); setQtde(Number(e.target.value)); }}>
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={50}>50</MenuItem>
            <MenuItem value={100}>100</MenuItem>
          </TextField>
          <Button variant="outlined" startIcon={<PictureAsPdfIcon />} onClick={exportPdf}>Exportar PDF</Button>
        </Stack>
        {data && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Exibindo {inicio}–{fim} de {totalItems} registro(s) · página {pagina}/{totalPages || 1}
          </Typography>
        )}
      </Paper>

      <ResponsiveTable>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={40} />
              <SortableTh id="dataHoraUtc" label="Data" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="responsavel" label="Responsável" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="modulo" label="Módulo" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="acao" label="Ação" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="campos" label="Campos" orderBy={orderBy} order={order} onSort={requestSort} />
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>Nenhum registro</TableCell>
              </TableRow>
            ) : sorted.map((item) => (
              <Fragment key={item.id}>
                <TableRow hover>
                  <TableCell>
                    <IconButton
                      size="small"
                      aria-label="Detalhes da alteração"
                      onClick={() => setExpanded((e) => ({ ...e, [item.id]: !e[item.id] }))}
                    >
                      {expanded[item.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </TableCell>
                  <TableCell data-label="Data">
                    {new Date(item.dataHoraUtc).toLocaleString('pt-BR')}
                  </TableCell>
                  <TableCell data-label="Responsável" sx={{ fontWeight: 600 }}>
                    {nomeResponsavel(item)}
                  </TableCell>
                  <TableCell data-label="Módulo">
                    {labelModulo(item.entidadeNome)}
                    {item.entidadeId ? (
                      <Typography component="span" variant="caption" color="text.secondary">
                        {` #${item.entidadeId}`}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell data-label="Ação">
                    <Chip size="small" color={acaoColor(item.acao)} label={ACAO_LABEL[item.acao?.toUpperCase()] || item.acao} />
                  </TableCell>
                  <TableCell data-label="Campos" sx={{ maxWidth: 420 }}>
                    <Typography variant="body2" color="text.secondary">
                      {montarCamposLista(item)}
                    </Typography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={6} sx={{ py: 0, border: 0 }}>
                    <Collapse in={!!expanded[item.id]}>
                      <Box sx={{ p: 2, bgcolor: 'action.hover', mb: 1, borderRadius: 1 }}>
                        <AlteracoesDetalhe item={item} />
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </Fragment>
            ))}
          </TableBody>
        </Table>
      </ResponsiveTable>

      <Stack alignItems="center" spacing={1} sx={{ mt: 2 }}>
        <Pagination
          count={totalPages}
          page={pagina}
          onChange={(_e, p) => setPagina(p)}
          color="primary"
          showFirstButton
          showLastButton
        />
      </Stack>
    </Box>
  );
}
